import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_N8N_URL || 'https://n8n.automationtekleabsam.com/webhook';
const STORAGE_KEY = 'authUser';

/**
 * Ce que chaque rôle a le droit de faire.
 *
 * ⚠️ Ces droits ne sont appliqués QUE dans l'interface : ils décident de ce qui
 * s'affiche, pas de ce qui est autorisé côté serveur. Les webhooks n8n restent
 * ouverts, donc un utilisateur averti peut les appeler directement. Voir
 * DROITS-UTILISATEURS.md pour ce qu'il reste à faire côté n8n.
 */
export const PERMISSIONS = {
  admin: ['review', 'stats', 'library', 'cards:write', 'assistant', 'settings', 'sync'],
  contributeur: ['library', 'cards:write', 'assistant'],
};

// Page d'atterrissage selon le rôle : un contributeur ne révise pas,
// l'accueil (qui propose la révision) n'a donc pas de sens pour lui.
export const homeRouteFor = (role) =>
  PERMISSIONS[role]?.includes('review') ? '/' : '/search';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restaurer la session au chargement
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.username && parsed?.role) {
          setUser(parsed);
        }
      }
    } catch (_) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    setIsLoading(false);
  }, []);

  /**
   * Les identifiants sont vérifiés par n8n contre la base Notion
   * « Utilisateurs ». Ils ne sont plus inscrits dans le code de
   * l'application, où n'importe qui pouvait les lire.
   */
  const login = async (username, password) => {
    if (!username?.trim() || !password) {
      return { success: false, error: 'Veuillez remplir tous les champs' };
    }

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });

      const data = await response.json();

      if (data?.success && data.user?.role) {
        const account = {
          username: data.user.username,
          displayName: data.user.displayName || data.user.username,
          role: PERMISSIONS[data.user.role] ? data.user.role : 'contributeur',
        };
        setUser(account);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(account));
        return { success: true, user: account };
      }

      return { success: false, error: data?.error || 'Identifiants incorrects' };
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return { success: false, error: 'Serveur injoignable. Réessayez.' };
    }
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const can = (permission) =>
    Boolean(user && PERMISSIONS[user.role]?.includes(permission));

  const value = {
    user,
    role: user?.role || null,
    isAuthenticated: Boolean(user),
    isLoading,
    login,
    logout,
    can,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
